import { apiClient, type Paginated } from '@/core/api';
import { triggerBlobDownload } from '@/features/finance/staff-invoices/api';
import type { AddInvoiceLineInput, Invoice, InvoiceInput, InvoiceProject } from './types';

export const BASE = '/finance/invoices/';

export interface InvoiceListParams {
  page?: number;
  search?: string;
  direction?: string;
  status?: string;
  company?: number;
  /** Issue-date range (YYYY-MM-DD) — quarter/month/custom via the period filter. */
  date_from?: string;
  date_to?: string;
  /** DRF `OrderingFilter` value, e.g. `number` or `-issue_date`. */
  ordering?: string;
}

export async function listInvoices(params: InvoiceListParams = {}): Promise<Paginated<Invoice>> {
  const { data } = await apiClient.get<Paginated<Invoice>>(BASE, { params });
  return data;
}

/** Suggested next client-invoice number (<prefix>-<year>-NNNN). */
export async function getNextInvoiceNumber(): Promise<string> {
  const { data } = await apiClient.get<{ number: string }>(`${BASE}next-number/`);
  return data.number;
}

export async function getInvoice(id: string | number): Promise<Invoice> {
  const { data } = await apiClient.get<Invoice>(`${BASE}${id}/`);
  return data;
}

/** Billable time in a draft invoice's projects + period that other invoices
 *  already billed — left out of this one (DEV13-77). */
export interface InvoiceBillingOverlap {
  entries: number;
  minutes: number;
  invoices: { id: number; public_id: string; number: string }[];
}

export async function getInvoiceBillingOverlap(
  id: string | number,
): Promise<InvoiceBillingOverlap> {
  const { data } = await apiClient.get<InvoiceBillingOverlap>(`${BASE}${id}/billing-overlap/`);
  return data;
}

/** Projects an invoice for `company` (a Company id, or '' for none) may link:
 *  every org project of that client plus company-less ones, private ones
 *  included — finance bills any project (BE ADR-101, ADR-102). */
export async function getInvoiceProjectOptions(company: string): Promise<InvoiceProject[]> {
  const { data } = await apiClient.get<{ results: InvoiceProject[] }>(`${BASE}project-options/`, {
    params: { company: company || 'none' },
  });
  return data.results;
}

export async function createInvoice(input: InvoiceInput): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(BASE, input);
  return data;
}

export async function updateInvoice(
  id: string | number,
  input: Partial<InvoiceInput>,
): Promise<Invoice> {
  const { data } = await apiClient.patch<Invoice>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteInvoice(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

/** POST /finance/invoices/{id}/add-line/ → returns the recalculated invoice. */
export async function addInvoiceLine(
  id: string | number,
  input: AddInvoiceLineInput,
): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/add-line/`, input);
  return data;
}

/** PATCH /finance/invoices/{id}/lines/{lineId}/ → edit a draft line in place
 * (description, and optionally quantity / unit_rate); returns the recalculated
 * invoice. */
export async function updateInvoiceLine(
  id: string | number,
  lineId: number,
  input: { description?: string; quantity?: string; unit_rate?: string },
): Promise<Invoice> {
  const { data } = await apiClient.patch<Invoice>(`${BASE}${id}/lines/${lineId}/`, input);
  return data;
}

/** DELETE /finance/invoices/{id}/lines/{lineId}/ → remove a draft line; returns
 * the recalculated invoice. */
export async function removeInvoiceLine(
  id: string | number,
  lineId: number,
): Promise<Invoice> {
  const { data } = await apiClient.delete<Invoice>(`${BASE}${id}/lines/${lineId}/`);
  return data;
}

/** POST /finance/invoices/{id}/lines/{lineId}/move/ → move a draft line one step
 * up/down; returns the reordered invoice. */
export async function moveInvoiceLine(
  id: string | number,
  lineId: number,
  direction: 'up' | 'down',
): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/lines/${lineId}/move/`, {
    direction,
  });
  return data;
}

/** POST /finance/invoices/{id}/pdf-inserts/ → upload PDFs to append to the doc. */
export async function uploadInvoicePdfInserts(
  id: string | number,
  files: File[],
): Promise<Invoice> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/pdf-inserts/`, form);
  return data;
}

/** DELETE /finance/invoices/{id}/pdf-inserts/{insertId}/ → remove an insert. */
export async function removeInvoicePdfInsert(
  id: string | number,
  insertId: number,
): Promise<Invoice> {
  const { data } = await apiClient.delete<Invoice>(`${BASE}${id}/pdf-inserts/${insertId}/`);
  return data;
}

/** POST /finance/invoices/{id}/issue/ → draft → issued (snapshot + audit). */
export async function issueInvoice(id: string | number): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/issue/`, {});
  return data;
}

/** POST /finance/invoices/{id}/revert-to-draft/ → issued → draft (correction). */
export async function revertInvoiceToDraft(id: string | number): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/revert-to-draft/`, {});
  return data;
}

/** Mark a receivable PAID — realizes the linked deal's sales commission. */
export async function markInvoicePaid(id: string | number): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/mark-paid/`, {});
  return data;
}

/** POST /finance/invoices/{id}/generate-pdf/ → renders + attaches the PDF. */
export async function generateInvoicePdf(id: string | number): Promise<Invoice> {
  const { data } = await apiClient.post<Invoice>(`${BASE}${id}/generate-pdf/`, {});
  return data;
}

/** Fetch the PDF as a blob (JWT/org headers attached) — a raw media `src`/link
 * 401s / fails CORS cross-origin. For inline preview and download. */
export async function getInvoiceBlob(id: string | number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${BASE}${id}/download/`, { responseType: 'blob' });
  return data;
}

export async function downloadInvoice(id: string | number, filename: string): Promise<void> {
  triggerBlobDownload(await getInvoiceBlob(id), filename);
}
